import { useId } from 'react';

/** A single option in a {@link Select}. */
export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

/** Props for {@link Select}. */
export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'style' | 'id'> {
  /** Visible label. Never omitted. */
  readonly label: string;
  /** The choices offered. */
  readonly options: readonly SelectOption[];
  /** Validation message. Presence switches the control into its error state. */
  readonly error?: string;
  /** Guidance shown below the control. */
  readonly hint?: string;
}

/**
 * A labelled dropdown built on the native `select`.
 *
 * Native rather than a custom listbox on purpose: it inherits keyboard behaviour, screen-reader
 * support and the platform's own picker on mobile, none of which a hand-rolled menu gets for free.
 *
 * @param props - Component props.
 * @returns The select element.
 */
export function Select({ label, options, error, hint, ...rest }: SelectProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div>
      <label className="gm-field-label" htmlFor={id}>
        {label}
      </label>
      <select
        {...rest}
        id={id}
        className="gm-select"
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint !== undefined && (
        <p id={hintId} className="gm-text-muted" style={{ margin: '4px 0 0', fontSize: '14px' }}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} className="gm-field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
